import moment from 'moment';

import {
  CommonFormDataToLegacyPayloadProps,
  CommonLegacySubmitAttachmentData,
  LegacyProposalRequestProps,
  mapSubmitDataUtils,
  submitLegacyProposal,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';

import { LegacyProposalAPI } from '../../../data';

import { CurrentEmploymentSalary, SalaryProposalFormData } from './';

// TODO: Correct the type
type SalarySubmitData_Components = {
  fSalaryComp1StartDate: string;
  fSalaryComp1Amount: number;
  fSalaryComp2StartDate: string;
  fSalaryComp2Amount: number;
  fSalaryComp3StartDate: string;
  fSalaryComp3Amount: number;
  fSalaryComp4StartDate: string;
  fSalaryComp4Amount: number;
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type SalarySubmitData = any & {
  fEmplId: number;
  fEmplSubUId?: string;
  fSalaryProposalStartDate: string;
  fSalaryProposalFrequency: string; // Enum
  fSalaryProposalSalary: number;
  fSalaryProposalChange: number;
  fSalaryProposalChangePct: number;
  fSalaryProposalChangeReason: string; // Enum
  fSalaryProposalManagerId: number;
  fSalaryProposalHigherMgrId: number;
  fSalaryProposalHrId: number;
  fSalaryProposalStatus: string; // Enum

  fSalaryProposalComponents: SalarySubmitData_Components;
} & CommonLegacySubmitAttachmentData;

export const mapFormDataToLegacyPayload = (
  props: CommonFormDataToLegacyPayloadProps<SalaryProposalFormData> & {
    enableHourlySalaryComponents: boolean;
    currentEmploymentSalary: CurrentEmploymentSalary;
  },
): SalarySubmitData | FormData => {
  const {
    data,
    isHRUser,
    decision,
    requestProps: { employeeId, proposalId },
    enableHourlySalaryComponents,
    currentEmploymentSalary,
  } = props;
  // TODO: Data mapping
  const {
    submissionResponsibleHR,
    submissionDateInFormat,
    submissionProposalStatus,
  } = mapSubmitDataUtils({ defaultSubmissionDateFormat: 'YYYY-MM-DD' });

  const isHourlyComponents =
    enableHourlySalaryComponents && data.salaryFrequency === 'HOURLY';
  const isRaise =
    !isHourlyComponents &&
    currentEmploymentSalary.salaryFrequency === data.salaryFrequency;

  const hourlyComps: SalarySubmitData_Components = Object.assign(
    {},
    ...(data?.hourlySalaryComponents?.map((component, index) => {
      const { amount, startDate } = component;

      const compNumber = index + 1;

      return {
        [`fSalaryComp${compNumber}StartDate`]:
          submissionDateInFormat(startDate),
        [`fSalaryComp${compNumber}Amount`]: amount ?? '',
      };
    }) || []),
  );

  const hourlyCompsStartDate =
    data?.hourlySalaryComponents
      ?.filter((c) => !!c.startDate)
      .map((c) => c.startDate)
      .sort((a, b) => moment(a).diff(moment(b)))[0] || null;

  const conditionalHourlyComponentsValues = isHourlyComponents
    ? {
        fSalaryProposalComponents: hourlyComps,
        fSalaryProposalStartDate: submissionDateInFormat(hourlyCompsStartDate),
        fSalaryProposalSalary: 0,
      }
    : {
        fSalaryProposalStartDate: submissionDateInFormat(data.startDate),
        fSalaryProposalSalary: data.newSalary,
      };

  const conditionalRaiseValues =
    isRaise && !isHourlyComponents
      ? {
          fSalaryProposalChange: data.raiseAmount,
          fSalaryProposalChangePct: data.raisePercentage,
        }
      : {
          fSalaryProposalChange: 0,
          fSalaryProposalChangePct: 0,
        };

  const payloadCommon = {
    fEmplId: +data?.employmentId || employeeId,
    fEmplSubUId: proposalId,
    ...conditionalHourlyComponentsValues,
    ...conditionalRaiseValues,
    fSalaryProposalEndDate: submissionDateInFormat(data.endDate),
    fSalaryProposalInfo: data.info || '',
    fSalaryProposalFrequency: data.salaryFrequency,
    fSalaryProposalSalaryType: data.salaryType || '',
    fSalaryProposalSalaryClass: data.salaryClass || '',
    fSalaryProposalChangeReason: data.changeReason, // Enum
    fSalaryProposalManagerId: -1,
    fSalaryProposalHigherMgrId: -1,
    fSalaryProposalHrId: submissionResponsibleHR(data.responsibleHR),
    fSalaryProposalStatus: submissionProposalStatus({
      isHRUser,
      decision,
    }),
  };

  // JSON Payload
  if (!data.attachment) {
    return {
      ...payloadCommon,
      fEmplAttachment: {
        fAttachmentId: data.attachmentId || -1,
        fAttachmentName: data.attachmentName || '',
      },
    };
  }

  // FormData payload
  const formData = new FormData();
  Object.entries(payloadCommon).forEach((sbData) => {
    if (sbData[1] !== null && sbData[1] !== undefined) {
      formData.append(
        sbData[0],
        typeof sbData[1] === 'string' ? sbData[1] : sbData[1].toString(),
      );
    }
  });
  formData.append('file', data.attachment);
  formData.append('fEmplAttachment.fAttachmentName', data.attachment.name);

  return formData;
};

export const submitSalaryProposal = async (
  data: SalarySubmitData,
  requestProps: LegacyProposalRequestProps,
) =>
  submitLegacyProposal<SalarySubmitData>(
    LegacyProposalAPI.SalaryProposal,
    data,
    requestProps,
  );
